import { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { MapPin, Calendar, ChevronRight } from 'lucide-react';
import { SheetShell } from '../../components/boothly/sheet-shell.jsx';
import { InsetGroup } from '../../components/boothly/inset-list.jsx';
import { SaleNumpad } from './sale-numpad.jsx';
import { fmtCurrency } from '../../lib/format.js';

const IOS_EASE = 'cubic-bezier(0.32,0.72,0,1)';

export function NeuerMarkttagSheet({ open, onOpenChange, onCreateSession, dark }) {
  const [location, setLocation] = useState('');
  const [date, setDate] = useState('');
  const [notes, setNotes] = useState('');
  // Standgebühr: null = keine angegeben (unterscheidbar von 0 = explizit kostenlos).
  const [boothFee, setBoothFee] = useState(null);

  // NumPad-Footer (Pattern aus preis-kalkulator-sheet.jsx): eingefrorener Seed, damit nicht jeder
  // Tastendruck über prefill zurückfließt und den Eingabe-Puffer zerstört (Single-Digit-Bug).
  const [padOpen, setPadOpen]         = useState(false);
  const [closingPad, setClosingPad]   = useState(false);
  const [padEntered, setPadEntered]   = useState(false);
  const [padH, setPadH]               = useState(0);
  const [numpadSeed, setNumpadSeed]   = useState(0);  // nur beim Öffnen gesetzt
  const [padSession, setPadSession]   = useState(0);  // Mount-Key → frischer Seed pro Öffnen
  const scrollRef       = useRef(null);
  const scrollTargetRef = useRef(null);
  const contentRef      = useRef(null);
  const footerRef       = useRef(null);

  useEffect(() => {
    if (open) {
      setLocation('');
      setDate('');
      setNotes('');
      setBoothFee(null);
      setPadOpen(false);
      setClosingPad(false);
      setPadEntered(false);
      setNumpadSeed(0);
    }
  }, [open]);

  // Öffnen: im nächsten Frame Transform-Slide ausfahren (translateY 100%→0).
  useEffect(() => {
    if (padOpen) {
      const r = requestAnimationFrame(() => setPadEntered(true));
      return () => cancelAnimationFrame(r);
    }
  }, [padOpen]);

  // NumPad-Inhaltshöhe messen → sauberer max-height-Kollaps beim Schließen.
  useLayoutEffect(() => {
    if (padOpen && contentRef.current) {
      const h = contentRef.current.scrollHeight;
      if (h && h !== padH) setPadH(h);
    }
  }, [padOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Standgebühr-Zeile beim Öffnen in Sicht scrollen — ausgelöst durch transitionend des Footers
  // (deterministisch: erst wenn die Öffnen-Animation fertig ist, stimmt die Scroll-Höhe).
  useEffect(() => {
    if (!padOpen) return;
    const el = scrollTargetRef.current;
    const footer = footerRef.current;
    if (!el) return;
    let done = false;
    const doScroll = () => { if (done) return; done = true; el.scrollIntoView({ block: 'nearest', behavior: 'smooth' }); };
    const onEnd = (e) => { if (e.target === footer) doScroll(); };
    footer?.addEventListener('transitionend', onEnd);
    const t = setTimeout(doScroll, 380); // Fallback bei verschlucktem Event
    return () => { footer?.removeEventListener('transitionend', onEnd); clearTimeout(t); };
  }, [padOpen]);

  if (!open) return null;

  const handleStartNow = () => {
    onCreateSession?.({ location, date, notes, booth_fee: boothFee, startNow: true });
    onOpenChange(false);
  };

  const handleSaveForLater = () => {
    onCreateSession?.({ location, date, notes, booth_fee: boothFee, startNow: false });
    onOpenChange(false);
  };

  const openFeePad = (el) => {
    scrollTargetRef.current = el ?? null;
    setClosingPad(false);
    setNumpadSeed(boothFee ?? 0);
    setPadSession((s) => s + 1);
    if (!padOpen) setPadEntered(false);
    setPadOpen(true);
  };

  // NumPad schließen: max-height kollabiert nach unten, dann unmount.
  const closeNumpad = () => {
    if (!padOpen) return;
    setClosingPad(true);
    setPadOpen(false);
    setTimeout(() => setClosingPad(false), 320);
  };

  const toggleFeePad = (el) => { if (padOpen) closeNumpad(); else openFeePad(el); };

  const writeActive = (v) => { if (!padOpen) return; setBoothFee(v); };

  const clearFee = () => { setBoothFee(null); closeNumpad(); };

  const padMounted = padOpen || closingPad;

  return (
    <SheetShell
      open={open}
      onClose={() => onOpenChange(false)}
      grouped
      testId="neuer-markttag-sheet"
      title="Neuer Markttag"
      footer={!padMounted && (
        <div className="space-y-2">
          <button
            onClick={handleStartNow}
            disabled={!location}
            className="h-[50px] w-full rounded-[14px] bg-[var(--accent-troedel)] text-[17px] font-semibold text-white shadow-[var(--shine-button)] active:scale-[0.98] disabled:opacity-40 disabled:active:scale-100"
            style={{ transition: 'transform 100ms var(--ease-out-ios), opacity 150ms' }}
          >
            Jetzt starten
          </button>
          <button
            onClick={handleSaveForLater}
            disabled={!location || !date}
            className="h-11 w-full rounded-[14px] text-[17px] font-semibold text-[var(--accent-troedel)] active:bg-[var(--fill-quaternary)] disabled:opacity-40"
          >
            Für später speichern
          </button>
        </div>
      )}
      rawFooter={padMounted && (
        <div
          ref={footerRef}
          data-testid="markttag-standgebuehr-numpad"
          className="shrink-0 overflow-hidden px-4"
          style={{
            maxHeight: closingPad ? '0px' : (padH ? `${padH}px` : '600px'),
            transform: padEntered ? 'translateY(0)' : 'translateY(100%)',
            transition: `transform 0.3s ${IOS_EASE}, max-height 0.3s ${IOS_EASE}`,
            boxShadow: 'inset 0 0.5px 0 var(--separator)',
          }}
        >
          <div ref={contentRef} className="pb-3 pt-1.5">
            <div className="flex items-center justify-between">
              <button
                type="button"
                data-testid="markttag-standgebuehr-clear"
                onClick={clearFee}
                className="h-11 px-1 text-[17px] text-[var(--accent-red)] active:opacity-60"
              >
                Entfernen
              </button>
              <button
                type="button"
                data-testid="markttag-standgebuehr-done"
                onClick={closeNumpad}
                className="h-11 px-1 text-[17px] font-semibold text-[var(--accent-troedel)] active:opacity-60"
              >
                Fertig
              </button>
            </div>
            <SaleNumpad key={padSession} hideDisplay prefill={numpadSeed} onValueChange={writeActive} />
          </div>
        </div>
      )}
    >
      <div ref={scrollRef} className="space-y-6 pb-4 pt-1">
        <InsetGroup surface="L2">
          <label className="flex h-[52px] items-center gap-3 px-4">
            <MapPin className="h-5 w-5 shrink-0 text-[var(--accent-troedel)]" />
            <input
              type="text"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="z.B. Frankfurt Sachsenhausen"
              aria-label="Ort"
              enterKeyHint="next"
              className="box-border h-full w-full min-w-0 max-w-full flex-1 bg-transparent text-[17px] outline-none placeholder:text-[var(--label-tertiary)]"
            />
          </label>
          {/* Datum — min-w-0/max-w-full verhindert das Überlaufen des nativen Date-Inputs (WebKit
              gibt ihm sonst eine intrinsische min-width inkl. Picker-Indicator). */}
          <label className="ml-4 flex h-[52px] items-center gap-3 border-t border-[var(--separator)] pr-4">
            <Calendar className="h-5 w-5 shrink-0 text-[var(--accent-troedel)]" />
            <span className="shrink-0 text-[17px]">Datum</span>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="box-border min-w-0 max-w-full flex-1 bg-transparent text-right text-[17px] text-[var(--label-secondary)] outline-none"
            />
          </label>
        </InsetGroup>

        {/* Standgebühr (optional) — Wert läuft live mit; Eingabe über NumPad-Footer */}
        <InsetGroup surface="L2" footer="Optional. Damit siehst du im Live-Markt, ab wann du Gewinn machst.">
          <button
            type="button"
            data-testid="markttag-standgebuehr-toggle"
            onClick={(e) => toggleFeePad(e.currentTarget)}
            className={`flex h-[52px] w-full items-center gap-3 px-4 text-left active:bg-[var(--fill-quaternary)] ${padOpen ? 'bg-[var(--tint-troedel)]' : ''}`}
            style={{ transition: 'background-color 150ms var(--ease-out-ios)' }}
          >
            <span className="flex-1 text-[17px]">Standgebühr</span>
            <span
              data-testid="markttag-standgebuehr-value"
              className={`num text-[17px] ${boothFee == null ? 'text-[var(--label-secondary)]' : 'font-semibold'}`}
            >
              {boothFee == null ? 'Keine' : fmtCurrency(boothFee)}
            </span>
            <ChevronRight
              className="h-4 w-4 text-[var(--label-tertiary)]"
              strokeWidth={2.6}
              style={{ transform: padOpen ? 'rotate(90deg)' : 'none', transition: 'transform 200ms var(--ease-out-ios)' }}
            />
          </button>
        </InsetGroup>

        <InsetGroup title="Notizen" surface="L2">
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Optional, z.B. Stand Nr. 42, neben dem Brunnen"
            rows={3}
            className="box-border block w-full min-w-0 max-w-full resize-none bg-transparent px-4 py-3 text-[17px] outline-none placeholder:text-[var(--label-tertiary)]"
          />
        </InsetGroup>
      </div>
    </SheetShell>
  );
}
